import React, { useState, useEffect } from 'react';
import { Card, CardContent, Chip } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBlog } from '../../hooks/blogs';
import { PiNotePencilDuotone } from "react-icons/pi";
import { BsTrash3 } from "react-icons/bs";
import DeleteDialog from '../../components/dialog';
import { deleteBlog } from '../../api/blog';
import toast from "react-hot-toast";
import axios from 'axios';
import DOMPurify from 'dompurify';
import { base_URL } from '../../config/config';
import { FaArrowLeft } from "react-icons/fa";


const BlogPreview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const blogId = id;

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  const { data } = useBlog(blogId);
  const { title, seoTitle, content, heroImageUrl, status, date, author, slug } = data ?? {};

  useEffect(() => {
    const fetchHtmlContent = async () => {
      if (!content) return;
      try {
        const response = await axios.post(`${base_URL}/api/blogs/getHtmlFromS3`, {
          fileName: content
        });


        const { body } = response.data;
        if (!body) throw new Error("No Body returned");
        setHtmlContent(body);
      } catch (error) {
        console.error("Error fetching HTML:", error);
        toast.error("Failed to load blog preview");
      }
    };

    fetchHtmlContent();
  }, [content]);

  const confirm = () => {
    deleteBlog(data?._id).then(resp => {
      if (resp?.error) toast.error(resp?.error);
      else {
        toast.success(resp?.message);
        navigate("/blogs");
      }
    });
    setShowConfirmation(false);
  };

  const statusStyles = {
    draft: {
      bg: 'bg-orange-100',
      text: 'text-orange-700'
    },
    published: {
      bg: 'bg-green-100',
      text: 'text-green-700'
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto p-4">
        <div className="mb-4">
				<button
					onClick={() => navigate(-1)}
					className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
				>
					<FaArrowLeft /> Back
				</button>
			</div>
        <Card className="shadow-lg rounded-xl overflow-hidden">
          {/* Hero Image */}
          <div className="relative h-64 w-full">
            <img
              src={heroImageUrl}
              alt="Hero"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 bg-black bg-opacity-60 w-full p-4">
              <h2 className="text-white text-2xl font-bold">{title}</h2>
              <p className="text-white text-sm">{seoTitle}</p>
            </div>
          </div>

          <CardContent className="p-6 space-y-4">
            {/* Status & Meta */}
            <div className="flex items-center justify-between">
              <Chip
                label={status}
                className={`${statusStyles[status]?.bg} ${statusStyles[status]?.text} font-semibold`}
              />
              <span className="text-sm text-gray-500">{date}</span>
            </div>

            {/* Author Info */}
            {author && (
              <div className="flex items-center gap-3">
                {author.avatarUrl && (
                  <img
                    src={author.avatarUrl}
                    alt="Author"
                    className="w-10 h-10 rounded-full"
                  />
                )}
                <p className="text-gray-700 font-medium">{author.name}</p>
              </div>
            )}

            {/* Blog Content Preview */}
            <div className="prose max-w-none">
              {htmlContent ? (
                <div
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }}
                />
              ) : (
                <p className="text-gray-500 italic">Loading preview...</p>
              )}
            </div>
          </CardContent>

          {/* Action Buttons */}
          <div className="flex w-full justify-end items-center space-x-3 me-6 py-4 px-4">
            <button
              className='flex gap-2 items-center px-4 py-2 bg-blue-500 text-white rounded'
              onClick={() => navigate(`/blogs/edit/${slug}`)}
            >
              <PiNotePencilDuotone /> Edit
            </button>
            <button
              className='flex gap-2 items-center px-4 py-2 bg-rose-500 text-white rounded'
              onClick={() => setShowConfirmation(true)}
            >
              <BsTrash3 /> Delete
            </button>
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirm}
      />
    </>
  );
};

export default BlogPreview;

